/**
 * Servicio para sincronización de stock Flexy WMS
 */
import axiosInstance from '../../api/axiosInstance'
import { DEFAULT_FLEXY_IDEMPRESA } from '../../constants/flexyStock'
import type {
  FlexyStockStatusResponse,
  FlexyStockSyncBody,
  FlexyStockSyncStartResponse,
  FlexyStockWarehouseSyncResponse,
} from '../../types/cron/flexyStock'

export class FlexyStockService {
  static readonly defaultIdempresa = DEFAULT_FLEXY_IDEMPRESA

  static async getStatus(
    idempresa: number = DEFAULT_FLEXY_IDEMPRESA,
    idalmacen?: number
  ): Promise<FlexyStockStatusResponse> {
    const response = await axiosInstance.get<FlexyStockStatusResponse>(
      '/flexy/stock/status',
      {
        params: {
          idempresa,
          ...(idalmacen != null ? { idalmacen } : {}),
        },
      }
    )
    return response.data
  }

  /**
   * Iniciar sync multi-bodega (una sola solicitud POST, HTTP 202).
   * El backend recorre todas las bodegas; el frontend solo hace polling de status.
   */
  static async startAllWarehousesSync(
    idempresa: number = DEFAULT_FLEXY_IDEMPRESA,
    body?: Pick<FlexyStockSyncBody, 'fecha' | 'force'>
  ): Promise<FlexyStockSyncStartResponse> {
    const response = await axiosInstance.post<FlexyStockSyncStartResponse>(
      '/flexy/stock/sync',
      {
        idempresa,
        ...body,
      },
      { validateStatus: (status) => status === 202 || status === 409 || status < 500 }
    )

    if (response.status === 409) {
      const err = new Error(
        response.data?.message ?? 'Ya hay una sincronización en curso'
      ) as Error & { code?: string; status?: number; isAlreadyRunning?: boolean }
      err.code = response.data?.code ?? 'FLEXY_SYNC_ALREADY_RUNNING'
      err.status = 409
      err.isAlreadyRunning = true
      throw err
    }

    if (response.status === 503) {
      throw new Error(
        response.data?.message ?? 'Sincronización Flexy no disponible'
      )
    }

    return response.data
  }

  /**
   * Sync síncrono de una sola bodega (HTTP 200).
   * Solo para ejecución manual puntual; no combinar con sync multi-bodega.
   */
  static async syncSingleWarehouse(
    idalmacen: number,
    idempresa: number = DEFAULT_FLEXY_IDEMPRESA,
    fecha?: string
  ): Promise<FlexyStockWarehouseSyncResponse> {
    try {
      const response = await axiosInstance.post<FlexyStockWarehouseSyncResponse>(
        '/flexy/stock/sync',
        {
          idempresa,
          idalmacen,
          force: true,
          ...(fecha ? { fecha } : {}),
        }
      )
      return response.data
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number; data?: { message?: string } } })
        ?.response?.status
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Error al sincronizar bodega'

      if (status === 429) {
        throw new Error('Otro sync está en curso. Intenta de nuevo en unos minutos.')
      }
      throw new Error(message)
    }
  }
}
